import { useMemo } from 'react'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Mail, PanelRight, Phone } from 'lucide-react'
import type { Contact, FieldDefinition } from '@/api/types'
import {
  COLUMNS,
  CUSTOM_COLUMN_PREFIX,
  PAGE_SIZES,
  customContactColumns,
  type ColumnDef,
  type ColumnId,
  type PageSize,
  type TableSettings,
} from '@/features/contacts/contactsTableSettings'
import Select from '@/components/ui/Select'
import ColumnHeaderMenu, { type SortDir } from '@/components/ui/ColumnHeaderMenu'
import TableCard from '@/components/layout/TableCard'
import { cn } from '@/lib/cn'
import { formatCustomFieldValue } from '@/lib/customField'
import { formatShortDate } from '@/lib/dealFormat'
import { pageItems } from '@/lib/pageItems'
import OwnerAvatar from '@/components/OwnerAvatar'
import ActionMenu, { type ActionItem } from '@/components/ui/ActionMenu'

export interface SortState {
  column: ColumnId
  dir: SortDir
}

/** A telephone link that ignores formatting (spaces, brackets) in the href. */
function telHref(num: string): string {
  return `tel:${num.replace(/[^\d+]/g, '')}`
}

/** Deterministic avatar tint from a contact id, so a contact keeps its colour across renders. */
const AVATAR_PALETTE = [
  'bg-violet-100 text-violet-600',
  'bg-teal-100 text-teal-600',
  'bg-emerald-100 text-emerald-600',
  'bg-amber-100 text-amber-600',
  'bg-sky-100 text-sky-600',
  'bg-rose-100 text-rose-600',
  'bg-indigo-100 text-indigo-600',
]
function avatarColor(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]
}

/** Colour a lead-source badge; well-known sources get a distinct tint, the rest a neutral one. */
function sourceBadge(source: string): string {
  const s = source.toLowerCase()
  if (s.includes('website')) return 'bg-sky-50 text-sky-600'
  if (s.includes('realestate')) return 'bg-red-50 text-red-600'
  if (s.includes('domain')) return 'bg-indigo-50 text-indigo-600'
  if (s.includes('referral')) return 'bg-emerald-50 text-emerald-600'
  if (s.includes('open')) return 'bg-amber-50 text-amber-600'
  if (s.includes('phone')) return 'bg-purple-50 text-purple-600'
  if (s.includes('form')) return 'bg-teal-50 text-teal-600'
  if (s.includes('walk')) return 'bg-gray-100 text-gray-600'
  return 'bg-gray-100 text-gray-600'
}

/** Short date + time for the last-activity cell, e.g. "9 Jul 2026 · 3:21 pm". */
function dateTime(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  return {
    date: formatShortDate(iso),
    time: d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }),
  }
}

/**
 * Server-driven contacts table with the same UX as the deals board table:
 * sortable columns (click cycles / ⋮ picks a direction), add/remove columns,
 * and offset pagination — sort, filters and paging all round-trip to the server.
 */
export default function ContactsTable({
  contacts,
  total,
  page,
  isLoading,
  sort,
  onCycleSort,
  onSortDir,
  settings,
  onSettingsChange,
  onPageChange,
  selected,
  onToggle,
  onToggleAll,
  memberName,
  memberPhoto,
  onOpen,
  onPreview,
  rowActions,
  fieldDefs,
}: {
  contacts: Contact[]
  total: number
  /** 0-based page index. */
  page: number
  isLoading: boolean
  sort: SortState
  onCycleSort: (column: ColumnId) => void
  onSortDir: (column: ColumnId, dir: SortDir) => void
  settings: TableSettings
  onSettingsChange: (next: TableSettings) => void
  onPageChange: (page: number) => void
  selected: Set<string>
  onToggle: (id: string) => void
  onToggleAll: () => void
  memberName: Map<string, string>
  /** Owner id → avatar photo URL (null = show initials). */
  memberPhoto?: Map<string, string | null>
  /** Name click: open the contact full-screen (same window). */
  onOpen: (id: string) => void
  /** Preview icon: open the contact in the side panel. */
  onPreview: (id: string) => void
  /** The row's ⋯ menu; no menu column when omitted. */
  rowActions?: (c: Contact) => ActionItem[]
  /** Contact custom-field definitions, for the 'custom.<key>' columns (display-only, not sortable). */
  fieldDefs: FieldDefinition[]
}) {
  const allColumns = useMemo<ColumnDef[]>(
    () => [...COLUMNS, ...customContactColumns(fieldDefs)],
    [fieldDefs],
  )
  const columnsById = useMemo(() => new Map(allColumns.map((c) => [c.id, c])), [allColumns])
  const fieldDefsByKey = useMemo(() => new Map(fieldDefs.map((d) => [d.fieldKey, d])), [fieldDefs])
  // A custom field that looks like a company/organisation — shown as secondary text under the name.
  const companyKey = useMemo(
    () => fieldDefs.find((d) => /company|organisation|organization|business/i.test(d.label) || /company|organisation/i.test(d.fieldKey))?.fieldKey,
    [fieldDefs],
  )

  // Visible columns in the user's chosen ORDER (dropping any whose def was removed).
  const visibleColumns = useMemo(
    () => settings.columns.map((id) => columnsById.get(id)).filter((c): c is ColumnDef => !!c),
    [settings.columns, columnsById],
  )

  // Columns not currently shown (built-in + custom) — offered under "Add column" in each header menu.
  const hiddenColumns = useMemo(
    () => allColumns.filter((c) => !settings.columns.includes(c.id)).map((c) => ({ id: c.id, label: c.label })),
    [allColumns, settings.columns],
  )

  function removeColumn(column: ColumnId) {
    onSettingsChange({ ...settings, columns: settings.columns.filter((c) => c !== column) })
  }

  function addColumn(column: ColumnId) {
    if (settings.columns.includes(column)) return
    // Append so the added column lands at the end; the user can reorder it from the ⋮ menu.
    onSettingsChange({ ...settings, columns: [...settings.columns, column] })
  }

  /** Move a visible column one place earlier/later; 'name' stays pinned first. */
  function moveColumn(column: ColumnId, dir: -1 | 1) {
    const cols = [...settings.columns]
    const i = cols.indexOf(column)
    const j = i + dir
    if (i < 0 || j < 0 || j >= cols.length) return
    if (cols[i] === 'name' || cols[j] === 'name') return
    ;[cols[i], cols[j]] = [cols[j], cols[i]]
    onSettingsChange({ ...settings, columns: cols })
  }

  function changePageSize(size: PageSize) {
    onPageChange(0)
    onSettingsChange({ ...settings, pageSize: size })
  }

  const pageCount = Math.max(1, Math.ceil(total / settings.pageSize))
  const uiPage = Math.min(page, pageCount - 1) + 1 // 1-based for display
  const start = (uiPage - 1) * settings.pageSize
  const pageIds = contacts.map((c) => c.id)
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id))

  function renderCell(c: Contact, column: ColumnId) {
    switch (column) {
      case 'name': {
        const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || '—'
        const company = companyKey ? String(c.custom[companyKey] ?? '') : ''
        const inits = ((c.firstName?.[0] ?? '') + (c.lastName?.[0] ?? '')).toUpperCase() || (c.email?.[0] ?? '?').toUpperCase()
        return (
          <td key={column} className="px-4 py-2.5">
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => onPreview(c.id)}
                title="Preview in side panel"
                aria-label="Preview"
                className="text-gray-300 hover:text-brand-primary opacity-0 group-hover:opacity-100 shrink-0"
              >
                <PanelRight size={14} />
              </button>
              <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold ${avatarColor(c.id)}`}>
                {inits}
              </span>
              <div className="min-w-0">
                <button onClick={() => onOpen(c.id)} className="text-brand-dark hover:text-brand-primary hover:underline text-left font-medium block truncate">
                  {name}
                </button>
                {company && <span className="text-xs text-gray-400 block truncate">{company}</span>}
              </div>
            </div>
          </td>
        )
      }
      case 'email':
        return (
          <td key={column} className="px-4 py-2.5 text-gray-600">
            {c.email ? (
              <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-brand-primary hover:underline">
                <Mail size={13} className="text-gray-400" /> {c.email}
              </a>
            ) : (
              '—'
            )}
          </td>
        )
      case 'phone':
        return (
          <td key={column} className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
            {c.phone ? (
              <a href={telHref(c.phone)} className="inline-flex items-center gap-1.5 hover:text-brand-primary hover:underline">
                <Phone size={13} className="text-gray-400" /> {c.phone}
              </a>
            ) : (
              '—'
            )}
          </td>
        )
      case 'mobile':
        return (
          <td key={column} className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
            {c.mobile ? (
              <a href={telHref(c.mobile)} className="inline-flex items-center gap-1.5 hover:text-brand-primary hover:underline">
                <Phone size={13} className="text-gray-400" /> {c.mobile}
              </a>
            ) : (
              '—'
            )}
          </td>
        )
      case 'owner': {
        const name = (c.ownerUserId && memberName.get(c.ownerUserId)) || null
        return (
          <td key={column} className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
            {name ? (
              <span className="inline-flex items-center gap-1.5">
                <OwnerAvatar name={name} photoUrl={(c.ownerUserId && memberPhoto?.get(c.ownerUserId)) || null} />
                {name}
              </span>
            ) : (
              <span className="text-gray-400">Unassigned</span>
            )}
          </td>
        )
      }
      case 'source':
        return (
          <td key={column} className="px-4 py-2.5 whitespace-nowrap">
            {c.leadSource ? (
              <span className={`text-[11px] font-medium rounded-md px-2 py-0.5 ${sourceBadge(c.leadSource)}`}>
                {c.leadSource}
              </span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </td>
        )
      case 'lastActivity':
        return (
          <td key={column} className="px-4 py-2.5 whitespace-nowrap">
            {c.lastContactedAt ? (
              <div className="leading-tight">
                <div className="text-brand-dark font-medium">{dateTime(c.lastContactedAt).date}</div>
                <div className="text-[11px] text-gray-400">{dateTime(c.lastContactedAt).time}</div>
              </div>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </td>
        )
      case 'nextContact':
        return (
          <td key={column} className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
            {c.nextContactDate ? formatShortDate(c.nextContactDate) : <span className="text-gray-400">—</span>}
          </td>
        )
      case 'openDeals':
        return (
          <td key={column} className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
            {c.openDeals > 0 ? c.openDeals : <span className="text-gray-400">0</span>}
          </td>
        )
      case 'created':
        return (
          <td key={column} className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
            {formatShortDate(c.createdAt)}
          </td>
        )
      default: {
        // custom.<key> column — value from the contact's custom map, formatted per its definition
        const def = column.startsWith(CUSTOM_COLUMN_PREFIX)
          ? fieldDefsByKey.get(column.slice(CUSTOM_COLUMN_PREFIX.length))
          : undefined
        const text = def ? formatCustomFieldValue(def, c.custom[def.fieldKey]) : null
        return (
          <td key={column} className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
            {text ?? '—'}
          </td>
        )
      }
    }
  }

  return (
    <TableCard
      footer={
        total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2.5">
            <span className="text-xs text-gray-500">
              Showing {(start + 1).toLocaleString('en-AU')}–
              {(start + contacts.length).toLocaleString('en-AU')} of {total.toLocaleString('en-AU')}
            </span>
            <div className="flex items-center gap-3">
              <nav className="flex items-center gap-0.5" aria-label="Pagination">
                <button
                  type="button"
                  onClick={() => onPageChange(uiPage - 2)}
                  disabled={uiPage <= 1}
                  aria-label="Previous page"
                  className="p-1 rounded text-gray-400 hover:text-brand-dark disabled:opacity-40 disabled:hover:text-gray-400"
                >
                  <ChevronLeft size={15} />
                </button>
                {pageItems(uiPage, pageCount).map((item, i) =>
                  item === '…' ? (
                    <span key={`gap-${i}`} className="px-1 text-xs text-gray-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => onPageChange(item - 1)}
                      aria-current={item === uiPage ? 'page' : undefined}
                      className={cn(
                        'min-w-6 px-1.5 py-1 rounded text-xs',
                        item === uiPage
                          ? 'bg-brand-primary/10 text-brand-dark font-medium'
                          : 'text-gray-500 hover:text-brand-dark',
                      )}
                    >
                      {item.toLocaleString('en-AU')}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  onClick={() => onPageChange(uiPage)}
                  disabled={uiPage >= pageCount}
                  aria-label="Next page"
                  className="p-1 rounded text-gray-400 hover:text-brand-dark disabled:opacity-40 disabled:hover:text-gray-400"
                >
                  <ChevronRight size={15} />
                </button>
              </nav>
              <Select
                size="xs"
                value={settings.pageSize}
                onChange={(e) => changePageSize(Number(e.target.value) as PageSize)}
                aria-label="Rows per page"
                className="w-28"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </Select>
            </div>
          </div>
        )
      }
    >
      <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase">
            <tr>
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={onToggleAll}
                  aria-label="Select all on this page"
                />
              </th>
              {visibleColumns.map((col, colIdx) => {
                const sortable = !!col.sortKey
                return (
                  <th
                    key={col.id}
                    className="px-4 py-2 group"
                    aria-sort={
                      sortable && sort.column === col.id
                        ? sort.dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-1">
                      {sortable ? (
                        <button
                          type="button"
                          onClick={() => onCycleSort(col.id)}
                          className="flex items-center gap-1 uppercase hover:text-brand-dark"
                          title="Sort"
                        >
                          {col.label}
                          {sort.column === col.id &&
                            (sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                        </button>
                      ) : (
                        <span className="uppercase" title="Custom column (not sortable)">{col.label}</span>
                      )}
                      <ColumnHeaderMenu
                        canRemove={!col.locked}
                        sortable={sortable}
                        onSort={(dir) => onSortDir(col.id, dir)}
                        onRemove={() => removeColumn(col.id)}
                        hiddenColumns={hiddenColumns}
                        onAdd={addColumn}
                        onMoveLeft={() => moveColumn(col.id, -1)}
                        onMoveRight={() => moveColumn(col.id, 1)}
                        canMoveLeft={colIdx > 1}
                        canMoveRight={colIdx > 0 && colIdx < visibleColumns.length - 1}
                        align={colIdx === 0 ? 'left' : 'right'}
                      />
                    </div>
                  </th>
                )
              })}
              {/* Pinned right so the ⋯ is reachable without scrolling a wide table sideways. */}
              {rowActions && <th className="sticky right-0 z-[1] bg-gray-50 px-2 py-2 w-10"><span className="sr-only">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {isLoading && contacts.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length + (rowActions ? 2 : 1)} className="px-4 py-6 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && total === 0 && (
              <tr>
                <td colSpan={visibleColumns.length + (rowActions ? 2 : 1)} className="px-4 py-6 text-center text-gray-400">
                  No contacts match your search or filters.
                </td>
              </tr>
            )}
            {contacts.map((c) => (
              <tr key={c.id} className="group border-t border-gray-100 hover:bg-gray-50/50">
                <td className="px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => onToggle(c.id)}
                    aria-label="Select contact"
                  />
                </td>
                {visibleColumns.map((col) => renderCell(c, col.id))}
                {rowActions && (
                  <td className="sticky right-0 z-[1] bg-white group-hover:bg-gray-50 px-2 py-2.5 text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]">
                    <ActionMenu label={`Actions for ${[c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'contact'}`} items={rowActions(c)} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
      </table>
    </TableCard>
  )
}
